import { HttpErrorResponse } from '@angular/common/http';

import { ConfigSchema, FieldDefinition } from '../models/provider.model';

/**
 * Client-side mirror of common's `ConfigValidator`, so the schema-driven feed form can flag what
 * core would reject before saving. Core stays the authority: it checks again on save and answers
 * a 400 whose `data` lists the same violations (see `configViolationsOf`).
 */

/** Core's `ConfigViolation.Code`. */
export type ConfigViolationCode =
  | 'UNKNOWN_KEY'
  | 'REQUIRED'
  | 'INVALID_FORMAT'
  | 'NOT_ALLOWED'
  | 'BELOW_MIN'
  | 'ABOVE_MAX'
  | 'PATTERN_MISMATCH';

/** One entry of a 400's `data` from a feed or subscription save. */
export interface ConfigViolation {
  key: string;
  code: ConfigViolationCode;
  message: string;
}

const INTEGER_FORMAT = /^-?(0|[1-9][0-9]*)$/;
const NUMBER_FORMAT = /^-?(0|[1-9][0-9]*)(\.[0-9]+)?$/;

/** Every field of a schema, in section order. */
export function schemaFields(schema: ConfigSchema): FieldDefinition[] {
  return schema.sections.flatMap((section) => section.fields);
}

/**
 * What's wrong with one field's value, or null. An absent or empty value means "use the
 * default", so it's only a problem for a required field.
 */
export function fieldProblem(field: FieldDefinition, value: string | undefined): ConfigViolationCode | null {
  if (value === undefined || value === '') {
    return field.required ? 'REQUIRED' : null;
  }
  if (!isCanonical(field, value)) {
    return 'INVALID_FORMAT';
  }
  if (field.type === 'enum' && !(field.values ?? []).includes(value)) {
    return 'NOT_ALLOWED';
  }
  if (field.type === 'integer' || field.type === 'number') {
    const number = Number(value);
    if (field.min !== undefined && number < field.min) return 'BELOW_MIN';
    if (field.max !== undefined && number > field.max) return 'ABOVE_MAX';
  }
  const pattern = field.pattern ? javaPattern(field.pattern) : null;
  if (pattern && !pattern.test(value)) {
    return 'PATTERN_MISMATCH';
  }
  return null;
}

/** Every problem with a configuration, by key — including keys the schema doesn't know. */
export function configProblems(schema: ConfigSchema, values: Record<string, string>): Record<string, ConfigViolationCode> {
  const problems: Record<string, ConfigViolationCode> = {};
  const fields = schemaFields(schema);
  for (const field of fields) {
    const problem = fieldProblem(field, values[field.key]);
    if (problem) problems[field.key] = problem;
  }
  for (const key of Object.keys(values)) {
    if (!fields.some((f) => f.key === key)) problems[key] = 'UNKNOWN_KEY';
  }
  return problems;
}

/** The violations in a feed/subscription save's 400, or null if the error isn't one. */
export function configViolationsOf(err: HttpErrorResponse): ConfigViolation[] | null {
  const data = err.status === 400 ? err.error?.data : null;
  return Array.isArray(data) && data.every((v) => typeof v?.key === 'string' && typeof v?.code === 'string')
    ? (data as ConfigViolation[])
    : null;
}

/** A readable label from a key code, for keys without a translation: `poll_interval_secs` →
 *  "Poll interval secs", `requestorRef` → "Requestor ref". */
export function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function isCanonical(field: FieldDefinition, value: string): boolean {
  switch (field.type) {
    case 'integer':
      return INTEGER_FORMAT.test(value);
    case 'number':
      return NUMBER_FORMAT.test(value);
    case 'boolean':
      return value === 'true' || value === 'false';
    case 'url':
      return isHttpUrl(value);
    default:
      return true;
  }
}

function isHttpUrl(value: string): boolean {
  if (value !== value.trim()) return false;
  try {
    const url = new URL(value);
    return (url.protocol === 'http:' || url.protocol === 'https:') && !!url.hostname;
  } catch {
    return false;
  }
}

const patternCache = new Map<string, RegExp | null>();

/**
 * A descriptor pattern (a Java regex the whole value must match) as a JS RegExp. A leading
 * `(?i)` becomes the `i` flag; a pattern JS can't compile returns null and is left to core.
 */
function javaPattern(pattern: string): RegExp | null {
  if (!patternCache.has(pattern)) {
    let source = pattern;
    let flags = '';
    if (source.startsWith('(?i)')) {
      source = source.slice(4);
      flags = 'i';
    }
    let compiled: RegExp | null;
    try {
      compiled = new RegExp(`^(?:${source})$`, flags);
    } catch {
      compiled = null;
    }
    patternCache.set(pattern, compiled);
  }
  return patternCache.get(pattern) ?? null;
}
